import { ColumnsSettings } from "../../../common/tables/ColumnsSelector/ColumnsSelector";
import {
  GenericVirtualizedTableCells,
  SortState,
} from "../../../common/tables/GenericVirtualizedTable/GenericVirtualizedTableTypes";
import { useContext, useMemo } from "react";
import { TableTabsDict } from "../../../common/tables/Tabs/TableTabsTypes";
import { useTabStore } from "../../../common/tables/Tabs/useTabStore";
import { LucideIcon } from "../../../common/icon/LucideIcon";
import TableView, { TableViewLabel } from "../../../common/panels/TableView/TableView";
import { EntityTable } from "../../../common/tables/EntityTable/EntityTable";
import { EntityFilterIndicator } from "../../../common/tables/EntityFilterIndicator/EntityFilterIndicator";
import { SearchInput } from "../../../common/forms/SearchInput/SearchInput";
import styles from "../../../common/tables/GenericVirtualizedTable/commons.module.css";
import { NotSet } from "../../../common/misc/UIconstants";
import {
  genericDefaultSortState,
  getAsyncSortingFn,
  getSortStateArray,
  UseEntityTableColumns,
  UseEntityTableDefaultProps,
  UseEntityTableProps,
  useGenericVirtualizedTable,
  useGenericVirtualizedTableTabs,
} from "../../../common/tables/GenericVirtualizedTable/useGenericVirtualizedTable";
import { EntityTableProps } from "../../../common/entity/EntityInterfaces";
import {
  CustomFieldTemplate,
  customFieldTemplateConstants,
  CustomFieldTemplateFilters,
  CustomFieldTemplateFiltersTranslator,
} from "../../../api/CustomFields/CustomFieldTemplates";
import { generateAbstractedFromDataType } from "../../CustomFields/CustomFieldUtils";
import { AbstractedCustomFieldDataTypeIcon } from "../../CustomFields/CustomFieldRenderUtils";
import { CustomFieldTemplatesFilterBar } from "./CustomFieldTemplatesFilterBar";
import { ToggleIndicator } from "../../../common/badges/ToggleIndicator/ToggleIndicator";
import { TableArrayRenderer } from "../../../common/misc/EntityRenders/EntityRenderer";
import { CustomFieldTableRenderer } from "../../CustomFields/CustomFieldRenderer";
import { CustomField } from "../../../api/CustomFields/CustomFields";
import { Button } from "../../../common/buttons/Button/Button";
import { useEntityPermissions } from "../../../common/permissions/useEntityPermissions";
import { SessionContext } from "../../../common/contexts/SessionContext";
import { showtoast } from "../../../common/overlays/Toasts/showtoast";
import { useQueryClient } from "@tanstack/react-query";
import { EntityOrderBy, InferSortByOptions } from "../../../api/GenericTypes";

export const switchCustomFieldTemplatesDefaultSortState = (
  sortState: EntityOrderBy<InferSortByOptions<CustomFieldTemplateFilters>>[] | null | undefined
): SortState<InferSortByOptions<CustomFieldTemplateFilters>>[] => {
  const state = genericDefaultSortState(sortState);
  if (state) return state;

  return getSortStateArray(sortState, (sort) => {
    switch (sort?.property) {
      case "DATATYPE":
        return {
          sortDirection: sort.sortDirection,
          headerId: "default-dataType",
          property: sort.property,
        };

      default:
        return {
          sortDirection: "ASC",
          headerId: "default-name",
          property: "NAME",
        };
    }
  });
};
export const defaultCustomFieldTemplatesFilters: CustomFieldTemplateFilters = {
  sortBy: [{ property: "NAME", sortDirection: "ASC" }],
};

export const useCustomFieldTemplatesTableDefaults = ({
  fieldLabels,
}: UseEntityTableDefaultProps<"customFieldTemplates">) => {
  const defaults: ColumnsSettings<CustomFieldTemplate> = useMemo(
    () => ({
      "default-id": { pos: 0, active: false, header: fieldLabels.id, property: "id" },
      "default-name": { pos: 1, active: true, header: fieldLabels.name, property: "name" },
      "default-dataType": { pos: 2, active: true, header: fieldLabels.dataType, property: "dataType" },
      "default-description": {
        pos: 3,
        active: true,
        header: fieldLabels.description,
        property: "description",
      },
      "default-placeholder": { pos: 4, active: true, header: fieldLabels.placeholder, property: "placeholder" },
      "default-required": { pos: 5, active: true, header: fieldLabels.required, property: "required" },
      "default-readOnly": { pos: 6, active: true, header: fieldLabels.readOnly, property: "readOnly" },
      "default-validationRegexp": {
        pos: 7,
        active: true,
        header: fieldLabels.validationRegexp,
        property: "validationRegexp",
      },
      "default-validationMessage": {
        pos: 8,
        active: true,
        header: fieldLabels.validationMessage,
        property: "validationMessage",
      },
      "default-predefinedOptions": {
        pos: 10,
        active: true,
        header: fieldLabels.predefinedOptions,
        property: "predefinedOptions",
      },
      "default-predefinedOptionsFromValues": {
        pos: 11,
        active: true,
        header: fieldLabels.predefinedOptionsFromValues,
        property: "predefinedOptionsFromValues",
      },
      "default-defaultValues": { pos: 12, active: true, header: fieldLabels.defaultValues, property: "defaultValues" },
      "default-integrations": { pos: 13, active: true, header: fieldLabels.integrations, property: "integrations" },
      "default-templateVersion": {
        pos: 14,
        active: true,
        header: fieldLabels.templateVersion,
        property: "templateVersion",
      },
    }),
    [fieldLabels]
  );

  const tabStoreDefaults: TableTabsDict<CustomFieldTemplate, CustomFieldTemplateFilters, any> = useMemo(
    () => ({
      default: {
        tabId: "default",
        type: "fixed",
        label: "All",
        title: "All",
        icon: "house",
        align: "left",
        xPos: 0,
        settings: {
          columnSettings: {},
          columnWidths: {},
          filters: defaultCustomFieldTemplatesFilters,
          sidebarFilters: {},
        },
        forcedSettings: {
          columnSettings: {},
          columnWidths: {},
          filters: {},
          sidebarFilters: {},
        },
      },
    }),
    []
  );
  return { defaults, tabStoreDefaults };
};

export const useCustomFieldTemplatesTableColumns: UseEntityTableColumns<"customFieldTemplates"> = ({
  entityConstants,
  fieldLabels,
  sort,
  setSort,
}: UseEntityTableProps<"customFieldTemplates">) => {
  const columns = useMemo(() => {
    let headers: GenericVirtualizedTableCells<CustomFieldTemplate> = [
      {
        id: "default-id",
        Header: fieldLabels.id,
        accessor: (row) => <span style={{ color: "var(--gray-400)" }}>{row.id}</span>,
        width: 120,
        minWidth: 120,
        align: "left",
        ...getAsyncSortingFn("ID", sort, setSort),
      },
      {
        Header: fieldLabels.name,
        id: "default-name",
        accessor: (row) => (
          <div className={`${styles.container_ellipsis} gap-5`}>
            <div className={`${styles.container_ellipsis}`}>
              <span className={styles.label_highlight}>
                <LucideIcon name={entityConstants.icon} color={"var(--primary)"} /> {row.name}{" "}
                {!!row.customFieldsFromCurrentTemplate?.length && <span className="label label-soft-info">In use</span>}
              </span>
            </div>
          </div>
        ),
        minWidth: 100,
        width: 250,
        align: "left",
        ...getAsyncSortingFn("NAME", sort, setSort),
      },
      {
        Header: fieldLabels.dataType,
        id: "default-dataType",
        accessor: (row) => {
          const _type = generateAbstractedFromDataType(row.dataType);
          return (
            <>
              {_type && (
                <div
                  className="flex row-nowrap align-center gap-5"
                  style={{ width: "100%" }}
                  title={_type.abstractedDataType}
                >
                  {_type.abstractedDataType && (
                    <>
                      <div className="flex align-center">
                        <AbstractedCustomFieldDataTypeIcon dataType={_type.abstractedDataType} />
                      </div>
                      <div
                        style={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          width: "max-content",
                          maxWidth: "100%",
                        }}
                      >
                        {_type.abstractedDataType}
                      </div>
                      {_type.isMulti && (
                        <label
                          className="label label-soft-info"
                          style={{
                            margin: 0,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          Multi
                        </label>
                      )}
                      {_type.isFloat && (
                        <label
                          className="label label-soft-info"
                          style={{
                            margin: 0,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          Floating point number
                        </label>
                      )}
                      {_type.isDateTime && (
                        <label
                          className="label label-soft-info"
                          style={{
                            margin: 0,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          Date/Time
                        </label>
                      )}
                      {_type.isDateTimeRange && (
                        <label
                          className="label label-soft-info"
                          style={{
                            margin: 0,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          Date/Time range
                        </label>
                      )}
                      {_type.isTimeRange && (
                        <label
                          className="label label-soft-info"
                          style={{
                            margin: 0,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          Time range
                        </label>
                      )}
                      {row.showAsTextArea && (
                        <label
                          className="label label-soft-info"
                          style={{
                            margin: 0,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          Text area
                        </label>
                      )}
                      {_type.entitySelectOption && (
                        <label
                          className="label label-soft-info"
                          style={{
                            margin: 0,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {_type.entitySelectOption}
                        </label>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          );
        },
        minWidth: 100,
        width: 250,
        align: "left",
        ...getAsyncSortingFn("DATATYPE", sort, setSort),
      },
      {
        Header: fieldLabels.description,
        id: "default-description",
        accessor: (row) => (
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.description || NotSet}
          </div>
        ),
        minWidth: 100,
        width: 500,
        align: "left",
      },
      {
        Header: fieldLabels.placeholder,
        id: "default-placeholder",
        accessor: (row) => (
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={row.placeholder}
          >
            {row.placeholder || NotSet}
          </div>
        ),
        minWidth: 100,
        width: 300,
        align: "left",
      },
      {
        Header: fieldLabels.required,
        id: "default-required",
        accessor: (row) => <ToggleIndicator enabled={row.required ?? false} labelSet="yes/no" />,
        minWidth: 100,
        width: 100,
        align: "left",
      },
      {
        Header: fieldLabels.readOnly,
        id: "default-readOnly",
        accessor: (row) => <ToggleIndicator enabled={row.readOnly ?? false} labelSet="yes/no" />,
        minWidth: 100,
        width: 100,
        align: "left",
      },
      {
        Header: fieldLabels.validationRegexp,
        id: "default-validationRegexp",
        accessor: (row) => (
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={row.validationRegexp}
          >
            {row.validationRegexp || NotSet}
          </div>
        ),
        minWidth: 100,
        width: 300,
        align: "left",
      },
      {
        Header: fieldLabels.validationMessage,
        id: "default-validationMessage",
        accessor: (row) => (
          <div
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={row.validationMessage}
          >
            {row.validationMessage || NotSet}
          </div>
        ),
        minWidth: 100,
        width: 300,
        align: "left",
      },
      {
        Header: fieldLabels.predefinedOptions,
        id: "default-predefinedOptions",
        accessor: (row) =>
          !!row.predefinedOptions ? (
            <TableArrayRenderer<string> values={row.predefinedOptions} arrayLength={5}>
              {(v) => v}
            </TableArrayRenderer>
          ) : (
            NotSet
          ),
        minWidth: 100,
        width: 300,
        align: "left",
      },
      {
        Header: fieldLabels.predefinedOptionsFromValues,
        id: "default-predefinedOptionsFromValues",
        accessor: (row) => <ToggleIndicator enabled={row.predefinedOptionsFromValues ?? false} labelSet="yes/no" />,
        minWidth: 100,
        width: 150,
        align: "left",
      },
      {
        Header: fieldLabels.defaultValues,
        id: "default-defaultValues",
        accessor: (row) =>
          !!row.defaultValues ? (
            <CustomFieldTableRenderer
              entity={row}
              title={fieldLabels.defaultValues}
              customfield={row as CustomField}
              value={row.defaultValues}
              includeCustomFieldId={false}
            />
          ) : (
            NotSet
          ),
        minWidth: 100,
        width: 300,
        align: "left",
      },
      {
        Header: fieldLabels.integrations,
        id: "default-integrations",
        accessor: (row) => (
          <TableArrayRenderer values={row.integrations} arrayLength={5}>
            {(e) => <span className="label label-soft-default">{e}</span>}
          </TableArrayRenderer>
        ),
        minWidth: 100,
        width: 200,
        align: "left",
      },
      {
        Header: fieldLabels.templateVersion,
        id: "default-templateVersion",
        accessor: (row) => <span>{row.templateVersion || NotSet}</span>,
        minWidth: 100,
        width: 150,
        align: "left",
      },
    ];

    return headers;
  }, [
    entityConstants.icon,
    fieldLabels.dataType,
    fieldLabels.defaultValues,
    fieldLabels.description,
    fieldLabels.predefinedOptions,
    fieldLabels.predefinedOptionsFromValues,
    fieldLabels.id,
    fieldLabels.integrations,
    fieldLabels.name,
    fieldLabels.placeholder,
    fieldLabels.readOnly,
    fieldLabels.required,
    fieldLabels.validationMessage,
    fieldLabels.validationRegexp,
    fieldLabels.templateVersion,
    setSort,
    sort,
  ]);
  return { columns };
};
export const CustomFieldTemplatesTable = ({
  entityApi,
  entityConstants,
  fieldLabels,
  permissions,
  routes,
}: EntityTableProps<"customFieldTemplates">) => {
  const { defaults, tabStoreDefaults } = useCustomFieldTemplatesTableDefaults({ fieldLabels });

  const { selection, resultsCount, onCountChange, onSelectionChange, onSelectionPermissions } =
    useGenericVirtualizedTable<CustomFieldTemplate>();

  const {
    filters,
    sidebarFilters,
    forcedFilters,
    columnSetting,
    columnWidths,
    dispatchTabStore,
    currentTab,
    tabsLoading,
  } = useTabStore<CustomFieldTemplate, CustomFieldTemplateFilters, any>({
    resource: entityConstants.resource,
    defaults: tabStoreDefaults,
  });

  const { functionRef, sort, setSort, searchValue, setSearchValue } = useGenericVirtualizedTableTabs({
    currentTab,
    tabsLoading,
    filters,
    switchSortState: switchCustomFieldTemplatesDefaultSortState,
    dispatchTabStore,
  });

  const { columns } = useCustomFieldTemplatesTableColumns({ entityConstants, fieldLabels, sort, setSort });

  const memoizedFilters = useMemo(() => ({ ...filters, ...forcedFilters }), [filters, forcedFilters]);

  const { canCreate } = useEntityPermissions({ entityTypeId: "customFields" });

  const { api } = useContext(SessionContext);
  const queryClient = useQueryClient();
  return (
    <TableView>
      <TableView.Head>
        <TableView.Head.Label>
          <TableViewLabel entityConstants={entityConstants} resultsCount={resultsCount} />
        </TableView.Head.Label>
      </TableView.Head>
      <TableView.Body>
        <TableView.Body.Sidebar>
          <CustomFieldTemplatesFilterBar
            initialValues={sidebarFilters}
            dispatchTabStore={dispatchTabStore}
            tabsLoading={tabsLoading}
            currentTab={currentTab}
          />
        </TableView.Body.Sidebar>
        <TableView.Body.Content>
          <EntityTable>
            <EntityTable.Controls
              style={{
                borderTop: "0px",
                borderRadius: "0px",
              }}
            >
              <EntityFilterIndicator<CustomFieldTemplate, CustomFieldTemplateFilters>
                filters={filters}
                excludeFilters={{}}
                translatorConsts={CustomFieldTemplateFiltersTranslator}
              />
              <SearchInput searchValue={searchValue} setSearchValue={setSearchValue} placeholder="Search" />
              <Button
                className="btn btn-primary flex align-center gap-5"
                onClick={() => {
                  const targetId = Array.from(selection)[0];
                  api
                    .post(`${customFieldTemplateConstants.resource}/${targetId}/create`, {})
                    .then((response) => {
                      showtoast("success", "Custom field created successfully.");
                      queryClient.invalidateQueries({ queryKey: [customFieldTemplateConstants.resource] });
                    })
                    .catch((e) => {
                      showtoast("error", e.message ?? "Failed to create custom field.");
                      console.error(e);
                    });
                }}
                disabled={!canCreate || selection.size !== 1}
                title={
                  !canCreate
                    ? "No permission to create custom fields"
                    : selection.size !== 1
                      ? "Select a single template to create a custom field"
                      : "Create custom field from selected template"
                }
              >
                <div>
                  <LucideIcon name="plus" />
                </div>
                Create
              </Button>
            </EntityTable.Controls>
            <EntityTable.Body<CustomFieldTemplate, CustomFieldTemplateFilters>
              functionRef={functionRef}
              entityConstants={entityConstants}
              filters={memoizedFilters}
              columns={columns}
              columnSelect
              columnSetting={columnSetting}
              columnWidths={columnWidths}
              defaultColumnSettings={defaults}
              dispatchTabStore={dispatchTabStore}
              setResultsCount={onCountChange}
              onSelectionChange={onSelectionChange}
              onSelectionPermissions={onSelectionPermissions}
              showPermissionColumn
              loading={tabsLoading}
            />
          </EntityTable>
        </TableView.Body.Content>
      </TableView.Body>
    </TableView>
  );
};
